/**
 * WHICH MODEL A PICKED ELEMENT CAME FROM, AND WHICH ASSET IS ITS (audit 2026-10-05, bug 2).
 *
 * Element numbers (dbIds) repeat across the models of a federated site: on the winery #268 is
 * the Landscape model's ground AND the Walls model's wall. A double-click read the element from
 * the main model and matched assets by number alone, so the Element panel showed another model's
 * element, and Create asset / Create ticket / Add IoT acted on it.
 *
 * The rule is the one the single-click path already uses (AutodeskViewer's selection handler):
 * the model comes from the hit itself, and an asset is the element's when its source file is
 * the hit model's file. On a one-model site, or when the model's file is unknown, the number
 * alone decides — exactly as before — so nothing changes where nothing was ambiguous.
 */

import { getPOIElementDbId, getPOISourceFile } from '../../utils/POIFieldExtractor';

export interface LoadedModelEntry {
  model?: any;
  name?: string | null;
}

/** The file name assets record as their source for this loaded model; null when unknown. */
export function sourceFileOfModel(
  model: any,
  loadedModels: LoadedModelEntry[],
  primary?: { model?: any; fileName?: string | null }
): string | null {
  if (!model) return null;
  const entry = loadedModels.find(
    (lm) => lm.model && (lm.model === model || (lm.model.id != null && lm.model.id === model.id))
  );
  if (entry?.name) return entry.name;
  if (primary?.model && model === primary.model) return primary.fileName || null;
  return null;
}

const sameFile = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && (a === b || a.toLowerCase() === b.toLowerCase());

/**
 * The asset bound to element `dbId`.
 *  - one-model site, or the element's model file unknown → the first asset with that number
 *    (the long-standing behaviour);
 *  - several models and the file known → the asset recorded from that file; failing that, an
 *    asset that never recorded a file (it can only be matched by number); otherwise none —
 *    another model's asset with the same number is NOT this element's.
 */
export function findAssetForElement(
  pois: any[],
  dbId: number | null | undefined,
  sourceFile?: string | null,
  multiModel = false
): any | null {
  if (dbId === null || dbId === undefined) return null;
  const matches = pois.filter((poi) => getPOIElementDbId(poi) === dbId);
  if (matches.length === 0) return null;
  if (!sourceFile || !multiModel) return matches[0];
  return matches.find((poi) => sameFile(getPOISourceFile(poi), sourceFile))
    || matches.find((poi) => !getPOISourceFile(poi))
    || null;
}
